import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { ArrowLeft, BookOpen, ChevronRight, Check, X, Info } from 'lucide-react';
import ExerciseList from '@/components/ExerciseList';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReadingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [exercises, setExercises] = useState([]);
  const [currentExercise, setCurrentExercise] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completedIds, setCompletedIds] = useState([]);

  useEffect(() => {
    fetchExercises();
  }, [user?.level]);

  const fetchExercises = async () => {
    try {
      const params = new URLSearchParams();
      params.append('exercise_type', 'reading');
      if (user?.level) params.append('level', user.level);
      
      const response = await axios.get(`${API}/exercises?${params}`);
      setExercises(response.data);
      if (response.data.length > 0) {
        const firstAvailable = response.data.find(e => !e.is_locked) || response.data[0];
        setCurrentExercise(firstAvailable);
      }
    } catch (error) {
      console.error('Error fetching exercises:', error);
      toast.error('Error al cargar los ejercicios');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectExercise = async (exercise) => {
    if (exercise.is_locked) {
      toast.error('Este ejercicio requiere Premium');
      return;
    }
    setCurrentExercise(exercise);
    setAnswers({});
    setSubmitted(false);
    setResult(null);
    
    // Track exercise view for statistics
    try {
      await axios.post(`${API}/exercises/${exercise.id}/track-view`);
    } catch (error) {
      // Silent fail - don't interrupt user experience
      console.log('Failed to track view');
    }
  };

  const handleAnswerChange = (questionId, answer) => {
    setAnswers(prev => ({ ...prev, [questionId]: answer }));
  };

  const handleSubmit = async () => {
    if (!currentExercise) return;
    
    try {
      const response = await axios.post(`${API}/exercises/submit`, {
        exercise_id: currentExercise.id,
        exercise_type: currentExercise.type,
        answers
      });
      setResult(response.data);
      setSubmitted(true);
      setCompletedIds(prev => [...prev, currentExercise.id]);
      toast.success(`¡Completado! Puntuación: ${response.data.score}%`);
    } catch (error) {
      console.error('Error submitting exercise:', error);
      toast.error('Error al enviar respuestas');
    }
  };

  const handleNextExercise = () => {
    const currentIndex = exercises.findIndex(e => e.id === currentExercise?.id);
    for (let i = currentIndex + 1; i < exercises.length; i++) {
      if (!exercises[i].is_locked) {
        setCurrentExercise(exercises[i]);
        setAnswers({});
        setSubmitted(false);
        setResult(null);
        return;
      }
    }
    toast.info('¡Has completado todos los ejercicios disponibles!');
  };

  const getPartType = () => {
    return currentExercise?.content?.part_type || 'standard';
  };

  // Render Part 1: Short Messages
  const renderPart1ShortMessages = () => {
    const questions = currentExercise.content.questions || [];
    return (
      <div className="space-y-6">
        <div className="bg-blue-50 border border-blue-200 p-4 rounded-sm">
          <div className="flex items-start gap-2">
            <Info className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900">{currentExercise.content.instructions}</p>
              <p className="text-sm text-blue-700 mt-1">{currentExercise.content.instructions_es}</p>
            </div>
          </div>
        </div>

        {questions.map((q, idx) => (
          <div key={q.id} className="bg-white border border-slate-200 p-4 rounded-sm">
            {q.image_url ? (
              /* New 3-column layout: Number | Image (with context above) | Options (with question above) */
              <div className="flex gap-3 items-start">
                {/* Column 1: Number (5%) */}
                <div className="shrink-0 font-bold text-slate-900 text-lg pt-1" style={{width: '5%', minWidth: '28px'}}>
                  {idx + 1}
                </div>
                {/* Column 2: Image with context above */}
                <div style={{width: q.image_orientation === 'portrait' ? '32%' : '44%'}} className="shrink-0">
                  {q.context && (
                    <p className="text-slate-600 italic text-sm mb-2">{q.context}</p>
                  )}
                  {q.context_es && (
                    <p className="text-xs text-slate-500 italic mb-2">{q.context_es}</p>
                  )}
                  <img src={q.image_url} alt="Question visual" className="w-full rounded border border-slate-200" />
                </div>
                {/* Column 3: Question + Options - vertically centered */}
                <div style={{width: q.image_orientation === 'portrait' ? '63%' : '51%'}} className="flex-1 min-w-0 flex flex-col justify-center self-center">
                  <p className="font-medium text-slate-900 mb-1 text-sm">{idx + 1}. {q.question}</p>
                  <p className="text-xs text-slate-500 mb-4">{q.question_es}</p>
                  <RadioGroup
                    value={answers[q.id]?.toString() || ''}
                    onValueChange={(value) => handleAnswerChange(q.id, parseInt(value))}
                    disabled={submitted}
                  >
                    {q.options.map((option, optIdx) => (
                      <div key={optIdx} className={`flex items-center space-x-2 p-2 rounded-sm border ${
                        submitted 
                          ? optIdx === q.correct 
                            ? 'bg-green-50 border-green-300' 
                            : answers[q.id] === optIdx 
                              ? 'bg-red-50 border-red-300' 
                              : 'border-slate-200'
                          : 'border-slate-200 hover:bg-slate-50'
                      }`}>
                        <RadioGroupItem value={optIdx.toString()} id={`${q.id}-${optIdx}`} />
                        <Label htmlFor={`${q.id}-${optIdx}`} className="flex-1 cursor-pointer text-sm">
                          {String.fromCharCode(65 + optIdx)}. {option}
                        </Label>
                        {submitted && optIdx === q.correct && <Check className="w-4 h-4 text-green-600" />}
                        {submitted && answers[q.id] === optIdx && optIdx !== q.correct && <X className="w-4 h-4 text-red-600" />}
                      </div>
                    ))}
                  </RadioGroup>
                </div>
              </div>
            ) : (
              /* Fallback: original stacked layout for questions without images */
              <>
                {q.context && (
                  <p className="text-slate-600 italic mb-3">{q.context}</p>
                )}
                {q.context_es && (
                  <p className="text-sm text-slate-500 italic mb-3">{q.context_es}</p>
                )}
                {q.notice_text && (
                  <div className="mb-4 p-4 bg-slate-50 border-l-4 border-slate-300 font-mono text-sm whitespace-pre-line">
                    {q.notice_text}
                  </div>
                )}
                <p className="font-medium text-slate-900 mb-1">{idx + 1}. {q.question}</p>
                <p className="text-sm text-slate-500 mb-4">{q.question_es}</p>
                <RadioGroup
                  value={answers[q.id]?.toString() || ''}
                  onValueChange={(value) => handleAnswerChange(q.id, parseInt(value))}
                  disabled={submitted}
                >
                  {q.options.map((option, optIdx) => (
                    <div key={optIdx} className={`flex items-center space-x-2 p-3 rounded-sm border ${
                      submitted 
                        ? optIdx === q.correct 
                          ? 'bg-green-50 border-green-300' 
                          : answers[q.id] === optIdx 
                            ? 'bg-red-50 border-red-300' 
                            : 'border-slate-200'
                        : 'border-slate-200 hover:bg-slate-50'
                    }`}>
                      <RadioGroupItem value={optIdx.toString()} id={`${q.id}-${optIdx}`} />
                      <Label htmlFor={`${q.id}-${optIdx}`} className="flex-1 cursor-pointer">
                        {String.fromCharCode(65 + optIdx)}. {option}
                      </Label>
                      {submitted && optIdx === q.correct && <Check className="w-5 h-5 text-green-600" />}
                      {submitted && answers[q.id] === optIdx && optIdx !== q.correct && <X className="w-5 h-5 text-red-600" />}
                    </div>
                  ))}
                </RadioGroup>
              </>
            )}
          </div>
        ))}
      </div>
    );
  };

  // Render Part 2: Matching
  const renderPart2Matching = () => {
    const { people, options, instructions, instructions_es } = currentExercise.content;
    return (
      <div className="space-y-6">
        <div className="bg-blue-50 border border-blue-200 p-4 rounded-sm">
          <div className="flex items-start gap-2">
            <Info className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900">{instructions}</p>
              <p className="text-sm text-blue-700 mt-1">{instructions_es}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6">
          {/* PEOPLE column */}
          <div className="space-y-4">
            <h3 className="font-bold text-slate-900 text-base border-b border-slate-300 pb-2">PERSON</h3>
            {people.map((person) => (
              <div key={person.id} className="bg-white border border-slate-200 p-4 rounded-sm">
                <p className="font-medium text-slate-900 mb-2">{person.name}</p>
                <p className="text-sm text-slate-600">{person.description}</p>
                <div className="mt-3">
                  <select
                    value={answers[person.id] || ''}
                    onChange={(e) => handleAnswerChange(person.id, e.target.value)}
                    disabled={submitted}
                    className={`w-full p-2 border rounded-sm text-sm ${
                      submitted
                        ? answers[person.id] === currentExercise.content.answers[person.id]
                          ? 'bg-green-50 border-green-300'
                          : 'bg-red-50 border-red-300'
                        : 'border-slate-300'
                    }`}
                  >
                    <option value="">Select / Selecciona</option>
                    {options.map(opt => (
                      <option key={opt.id} value={opt.id}>{opt.id}</option>
                    ))}
                  </select>
                  {submitted && (
                    <p className={`text-sm mt-1 ${answers[person.id] === currentExercise.content.answers[person.id] ? 'text-green-600' : 'text-red-600'}`}>
                      Correct answer: {currentExercise.content.answers[person.id]}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* LOCATION column */}
          <div className="space-y-4">
            <h3 className="font-bold text-slate-900 text-base border-b border-slate-300 pb-2">WORKSPACE OPTIONS</h3>
            {options.map(opt => (
              <div key={opt.id} className="bg-slate-50 border border-slate-200 p-4 rounded-sm">
                <p className="font-bold text-orange-600">{opt.id}. {opt.title}</p>
                <p className="text-sm text-slate-600 mt-1">{opt.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Render Part 5: Long Text Multiple Choice (B2) - Text on top, questions in 2-column grid
  const renderPart3LongText = () => {
    const { text, questions, instructions, instructions_es, title } = currentExercise.content;
    return (
      <div className="space-y-4">
        <div className="bg-blue-50 border border-blue-200 p-3 rounded-sm">
          <div className="flex items-start gap-2">
            <Info className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900 text-sm">{instructions}</p>
              <p className="text-xs text-blue-700 mt-1">{instructions_es}</p>
            </div>
          </div>
        </div>

        {/* Side-by-side: sticky text left, questions right */}
        <div className="flex gap-4 items-start">
          {/* Text - sticky */}
          <div className="w-1/2 sticky top-20 self-start">
            <div className="bg-white border border-slate-200 p-5 rounded-sm max-h-[70vh] overflow-y-auto">
              {title ? (
                <h3 className="font-bold text-base text-slate-900 mb-3 pb-2 border-b border-slate-200">
                  {title}
                </h3>
              ) : (
                <h3 className="font-semibold text-slate-900 mb-3 text-sm">Text / Texto</h3>
              )}
              <div className="prose prose-sm max-w-none text-slate-700 whitespace-pre-line text-sm leading-relaxed">
                {text}
              </div>
            </div>
          </div>

          {/* Questions */}
          <div className="w-1/2">
            <div className="bg-white border border-slate-200 p-4 rounded-sm">
              <h3 className="font-semibold text-slate-900 text-sm mb-4">Questions / Preguntas</h3>
              <div className="space-y-4">
                {questions.map((q) => {
                  const questionNum = q.number || 31;
                  return (
                    <div key={q.id} className="border border-slate-200 rounded p-3 bg-slate-50">
                      <p className="font-medium text-slate-900 mb-1 text-sm">{questionNum}. {q.question}</p>
                      {q.question_es && <p className="text-xs text-slate-500 mb-2">{q.question_es}</p>}
                      
                      <RadioGroup
                        value={answers[q.id]?.toString() || ''}
                        onValueChange={(value) => handleAnswerChange(q.id, parseInt(value))}
                        disabled={submitted}
                        className="space-y-1"
                      >
                        {q.options.map((option, optIdx) => {
                          const isCorrect = optIdx === q.correct;
                          const isSelected = answers[q.id] === optIdx;
                          return (
                            <div key={optIdx} className={`flex items-start gap-2 p-2 rounded border ${
                              submitted 
                                ? isCorrect 
                                  ? 'bg-green-50 border-green-300' 
                                  : isSelected 
                                    ? 'bg-red-50 border-red-300' 
                                    : 'border-slate-200 bg-white'
                                : 'border-slate-200 bg-white hover:bg-slate-100'
                            }`}>
                              <RadioGroupItem value={optIdx.toString()} id={`${q.id}-${optIdx}`} className="mt-0.5" />
                              <Label htmlFor={`${q.id}-${optIdx}`} className="flex-1 cursor-pointer text-xs leading-relaxed">
                                {String.fromCharCode(65 + optIdx)}. {option}
                              </Label>
                            </div>
                          );
                        })}
                      </RadioGroup>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render Gapped Text (B2 Part 6, C1 Part 7) - Two columns: Paragraphs left, Dropdowns right
  // Supports both B2 format (text_with_gaps + sentences) and C1 format (text_sections + paragraphs)
  const renderPart4GappedText = () => {
    const { text_with_gaps, text_sections, sentences, paragraphs, answers: correctAnswers, instructions, instructions_es, title } = currentExercise.content;
    
    // Get gap numbers dynamically from correctAnswers
    const gapNumbers = Object.keys(correctAnswers).sort((a, b) => parseInt(a) - parseInt(b));
    
    // Use paragraphs (C1) or sentences (B2) for the options
    const optionsList = paragraphs || sentences || [];
    
    // Render text content - handle both formats
    const renderTextContent = () => {
      if (text_sections && Array.isArray(text_sections)) {
        // C1 format: text_sections array with gap markers
        return (
          <div className="space-y-4">
            {text_sections.map((section, idx) => {
              if (section.gap) {
                // This is a gap marker
                return (
                  <div key={section.id} className="flex items-center gap-2 py-2">
                    <span className="bg-orange-100 text-orange-700 px-3 py-1 rounded font-bold text-sm">
                      Gap {section.gap_number}
                    </span>
                  </div>
                );
              } else {
                // This is text content
                return (
                  <p key={section.id} className="text-sm text-slate-700 leading-relaxed">
                    {section.text}
                  </p>
                );
              }
            })}
          </div>
        );
      } else if (text_with_gaps) {
        // B2 format: single text string with gaps
        return (
          <div className="prose prose-sm max-w-none text-slate-700 whitespace-pre-line text-sm leading-relaxed">
            {text_with_gaps}
          </div>
        );
      }
      return null;
    };
    
    return (
      <div className="space-y-4">
        <div className="bg-blue-50 border border-blue-200 p-3 rounded-sm">
          <div className="flex items-start gap-2">
            <Info className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900 text-sm">{instructions}</p>
              <p className="text-xs text-blue-700 mt-1">{instructions_es}</p>
            </div>
          </div>
        </div>

        {/* Text with Gaps */}
        <div className="bg-white border border-slate-200 p-4 rounded-sm max-h-96 overflow-y-auto">
          {title ? (
            <h3 className="font-bold text-base text-slate-900 mb-3 pb-2 border-b border-slate-200">
              {title}
            </h3>
          ) : (
            <h3 className="font-semibold text-slate-900 mb-3 text-sm">Text / Texto</h3>
          )}
          {renderTextContent()}
        </div>

        {/* Two-column layout: Paragraphs/Sentences left, Answers right */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Left Column: Paragraphs/Sentences A-G */}
          <div className="bg-white border border-slate-200 p-4 rounded-sm max-h-96 overflow-y-auto">
            <h3 className="font-semibold text-slate-900 text-sm mb-3 pb-2 border-b border-slate-200">
              {paragraphs ? 'Paragraphs / Párrafos' : 'Sentences / Oraciones'}
            </h3>
            <div className="space-y-3">
              {optionsList.map(item => (
                <div key={item.id} className="flex gap-2 p-2 bg-slate-50 rounded border border-slate-200">
                  <span className="font-bold text-orange-600 min-w-6">{item.id}.</span>
                  <p className="text-sm text-slate-700">{item.text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right Column: Answer Dropdowns */}
          <div className="bg-white border border-slate-200 p-4 rounded-sm">
            <h3 className="font-semibold text-slate-900 text-sm mb-3 pb-2 border-b border-slate-200">
              Your Answers / Tus Respuestas
            </h3>
            <div className="space-y-3">
              {gapNumbers.map(gap => {
                const isCorrect = submitted && answers[gap] === correctAnswers[gap];
                return (
                  <div key={gap} className="flex items-center gap-3 p-2 bg-slate-50 rounded border border-slate-200">
                    <span className="font-bold text-slate-900 min-w-8">{gap}.</span>
                    <select
                      value={answers[gap] || ''}
                      onChange={(e) => handleAnswerChange(gap, e.target.value)}
                      disabled={submitted}
                      className={`flex-1 p-2 border rounded text-sm ${
                        submitted
                          ? isCorrect
                            ? 'bg-green-50 border-green-400'
                            : 'bg-red-50 border-red-400'
                          : 'border-slate-300 bg-white'
                      }`}
                    >
                      <option value="">A-G</option>
                      {optionsList.map(item => (
                        <option key={item.id} value={item.id}>{item.id}</option>
                      ))}
                    </select>
                    {submitted && (
                      <span className={`text-sm font-medium ${isCorrect ? 'text-green-600' : 'text-red-600'}`}>
                        ✓ {correctAnswers[gap]}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render Part 1: Multiple Choice Cloze (B2) - Two blocks (1-4, 5-8) with 4-column grid
  // Options shown with full text next to radio button
  const renderPart5Cloze = () => {
    const { text, questions, instructions, instructions_es, title } = currentExercise.content;
    
    // Split questions into two rows of three: 21-23 and 24-26
    const row1 = questions.slice(0, 3);
    const row2 = questions.slice(3, 6);
    
    const renderQuestionCard = (q) => (
      <div key={q.id} className="border border-slate-200 rounded p-3 bg-slate-50">
        <RadioGroup
          value={answers[q.id]?.toString() || ''}
          onValueChange={(value) => handleAnswerChange(q.id, parseInt(value))}
          disabled={submitted}
          className="space-y-1"
        >
          {q.options.map((option, optIdx) => {
            const isCorrect = optIdx === q.correct;
            const isSelected = answers[q.id] === optIdx;
            return (
              <div key={optIdx} className={`flex items-center gap-2 p-1.5 rounded ${
                submitted 
                  ? isCorrect 
                    ? 'bg-green-100' 
                    : isSelected 
                      ? 'bg-red-100' 
                      : ''
                  : 'hover:bg-white cursor-pointer'
              }`}>
                {optIdx === 0 ? (
                  <span className="font-bold text-slate-900 min-w-6">({q.gap_number})</span>
                ) : (
                  <span className="min-w-6"></span>
                )}
                <RadioGroupItem value={optIdx.toString()} id={`${q.id}-${optIdx}`} className="h-4 w-4 flex-shrink-0" />
                <Label htmlFor={`${q.id}-${optIdx}`} className="cursor-pointer text-sm flex-1">
                  {String.fromCharCode(65 + optIdx)}. {option}
                </Label>
                {submitted && isCorrect && <span className="text-green-600 text-xs">&#10003;</span>}
              </div>
            );
          })}
        </RadioGroup>
      </div>
    );

    return (
      <div className="space-y-4">
        <div className="bg-blue-50 border border-blue-200 p-3 rounded-sm">
          <div className="flex items-start gap-2">
            <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-blue-900 text-sm">{instructions}</p>
              <p className="text-xs text-blue-700 mt-1">{instructions_es}</p>
            </div>
          </div>
        </div>

        {/* Text */}
        <div className="bg-white border border-slate-200 p-4 rounded-sm">
          {title && (
            <h3 className="font-bold text-base text-slate-900 mb-3 pb-2 border-b border-slate-200">
              {title}
            </h3>
          )}
          <div className="prose prose-sm max-w-none text-slate-700 whitespace-pre-line text-sm leading-relaxed">
            {text}
          </div>
        </div>

        {/* Questions: 2 rows of 3 */}
        <div className="bg-white border border-slate-200 p-4 rounded-sm space-y-4">
          <div className="grid grid-cols-3 gap-4">
            {row1.map(renderQuestionCard)}
          </div>
          <div className="grid grid-cols-3 gap-4">
            {row2.map(renderQuestionCard)}
          </div>
        </div>
      </div>
    );
  };

  // Render Part 2: Open Cloze (B2) - 4-column grid for questions
  const renderPart6OpenCloze = () => {
    const { text, answers: correctAnswers, instructions, instructions_es, title } = currentExercise.content;
    // Get gap numbers dynamically from the answers object
    const gapNumbers = Object.keys(correctAnswers).sort((a, b) => parseInt(a) - parseInt(b));
    
    return (
      <div className="space-y-4">
        <div className="bg-blue-50 border border-blue-200 p-3 rounded-sm">
          <div className="flex items-start gap-2">
            <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-blue-900 text-sm">{instructions}</p>
              <p className="text-xs text-blue-700 mt-1">{instructions_es}</p>
            </div>
          </div>
        </div>

        {/* Text */}
        <div className="bg-white border border-slate-200 p-4 rounded-sm">
          {title && (
            <h3 className="font-bold text-base text-slate-900 mb-3 pb-2 border-b border-slate-200">
              {title}
            </h3>
          )}
          <div className="prose prose-sm max-w-none text-slate-700 whitespace-pre-line text-sm leading-relaxed">
            {text}
          </div>
        </div>

        {/* Answers in 4-column grid */}
        <div className="bg-white border border-slate-200 p-4 rounded-sm">
          <h3 className="font-semibold text-slate-900 text-sm mb-4">
            Write one word / Escribe una palabra
          </h3>
          <div className="grid grid-cols-3 gap-3">
            {gapNumbers.slice(0, 3).map(gap => {
              const acceptable = currentExercise.content.acceptable_answers?.[gap];
              const isCorrect = submitted && (
                correctAnswers[gap].toLowerCase().split('/').includes(answers[gap]?.toLowerCase()) ||
                (acceptable && acceptable.some(a => a.toLowerCase() === answers[gap]?.toLowerCase()))
              );
              return (
                <div key={gap} className="text-center">
                  <p className="font-bold text-slate-900 mb-2 text-base">({gap})</p>
                  <Input
                    value={answers[gap] || ''}
                    onChange={(e) => handleAnswerChange(gap, e.target.value.toLowerCase())}
                    disabled={submitted}
                    placeholder="..."
                    className={`w-full h-10 text-center text-sm ${
                      submitted
                        ? isCorrect
                          ? 'bg-green-50 border-green-400'
                          : 'bg-red-50 border-red-400'
                        : ''
                    }`}
                  />
                  {submitted && (
                    <p className={`text-xs mt-1 ${isCorrect ? 'text-green-600' : 'text-red-600'}`}>
                      {correctAnswers[gap]}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
          <div className="grid grid-cols-3 gap-3 mt-4">
            {gapNumbers.slice(3, 6).map(gap => {
              const acceptable = currentExercise.content.acceptable_answers?.[gap];
              const isCorrect = submitted && (
                correctAnswers[gap].toLowerCase().split('/').includes(answers[gap]?.toLowerCase()) ||
                (acceptable && acceptable.some(a => a.toLowerCase() === answers[gap]?.toLowerCase()))
              );
              return (
                <div key={gap} className="text-center">
                  <p className="font-bold text-slate-900 mb-2 text-base">({gap})</p>
                  <Input
                    value={answers[gap] || ''}
                    onChange={(e) => handleAnswerChange(gap, e.target.value.toLowerCase())}
                    disabled={submitted}
                    placeholder="..."
                    className={`w-full h-10 text-center text-sm ${
                      submitted
                        ? isCorrect
                          ? 'bg-green-50 border-green-400'
                          : 'bg-red-50 border-red-400'
                        : ''
                    }`}
                  />
                  {submitted && (
                    <p className={`text-xs mt-1 ${isCorrect ? 'text-green-600' : 'text-red-600'}`}>
                      {correctAnswers[gap]}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // Render Word Formation (B2 Part 3, C1 Part 3) - Continuous text with 4-column grid for answers
  // Supports both B2 format (base_words object) and C1 format (questions array with root_word)
  const renderWordFormation = () => {
    const { text, base_words, answers: correctAnswersObj, questions, instructions, instructions_es, title } = currentExercise.content;
    
    // Handle both B2 format (base_words object) and C1 format (questions array)
    let gapData = [];
    if (questions && Array.isArray(questions) && questions.length > 0 && questions[0].root_word) {
      // C1 format: questions array with root_word and correct_answer
      gapData = questions.map(q => ({
        gap: q.gap_number.toString(),
        rootWord: q.root_word,
        correctAnswer: q.correct_answer,
        id: q.id
      }));
    } else if (base_words) {
      // B2 format: base_words and answers objects
      const gapNumbers = Object.keys(base_words).sort((a, b) => parseInt(a) - parseInt(b));
      gapData = gapNumbers.map(gap => ({
        gap,
        rootWord: base_words[gap],
        correctAnswer: correctAnswersObj?.[gap] || '',
        id: `wf_${gap}`
      }));
    }
    
    return (
      <div className="space-y-4">
        <div className="bg-blue-50 border border-blue-200 p-3 rounded-sm">
          <div className="flex items-start gap-2">
            <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-blue-900 text-sm">{instructions}</p>
              <p className="text-xs text-blue-700 mt-1">{instructions_es}</p>
            </div>
          </div>
        </div>

        {/* Text as continuous paragraph */}
        <div className="bg-white border border-slate-200 p-4 rounded-sm">
          {title && (
            <h3 className="font-bold text-base text-slate-900 mb-3 pb-2 border-b border-slate-200">
              {title}
            </h3>
          )}
          <div className="prose prose-sm max-w-none text-slate-700 whitespace-pre-line text-sm leading-relaxed">
            {text}
          </div>
        </div>

        {/* Answers in 4-column grid */}
        <div className="bg-white border border-slate-200 p-4 rounded-sm">
          <h3 className="font-semibold text-slate-900 text-sm mb-4">
            Form the correct word / Forma la palabra correcta
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {gapData.map(({ gap, rootWord, correctAnswer, id }) => {
              const userAnswer = answers[id] || answers[gap] || '';
              const isCorrect = submitted && correctAnswer.toLowerCase().split('/').includes(userAnswer.toLowerCase());
              return (
                <div key={id} className="text-center border border-slate-200 rounded p-3 bg-slate-50">
                  <p className="font-bold text-slate-900 text-base mb-1">({gap})</p>
                  <p className="text-orange-600 font-mono uppercase text-xs mb-2">{rootWord}</p>
                  <Input
                    value={userAnswer}
                    onChange={(e) => handleAnswerChange(id, e.target.value.toLowerCase())}
                    disabled={submitted}
                    placeholder="..."
                    className={`w-full h-9 text-center text-sm ${
                      submitted
                        ? isCorrect
                          ? 'bg-green-50 border-green-400'
                          : 'bg-red-50 border-red-400'
                        : 'bg-white'
                    }`}
                  />
                  {submitted && (
                    <p className={`text-xs mt-1 ${isCorrect ? 'text-green-600' : 'text-red-600'}`}>
                      ✓ {correctAnswer}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // Render Key Word Transformation (B2 Part 4, C1 Part 4) - 2-column grid layout
  // Supports both B2 format (sentence1, keyword, answer) and C1 format (first_sentence, key_word, correct_answer)
  const renderKeyWordTransformation = () => {
    const { questions, instructions, instructions_es } = currentExercise.content;
    
    return (
      <div className="space-y-4">
        <div className="bg-blue-50 border border-blue-200 p-3 rounded-sm">
          <div className="flex items-start gap-2">
            <Info className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900 text-sm">{instructions}</p>
              <p className="text-xs text-blue-700 mt-1">{instructions_es}</p>
            </div>
          </div>
        </div>

        {/* Questions in 2-column grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {questions.map((q, idx) => {
            // Handle both B2 format (sentence1, keyword, answer) and C1 format (first_sentence, key_word, correct_answer)
            const firstSentence = q.first_sentence || q.sentence1;
            const keyWord = q.key_word || q.keyword;
            const correctAnswer = q.correct_answer || q.answer;
            const questionNumber = q.question_number || q.number || (25 + idx);
            
            // Handle both sentence2 format and second_sentence_start/second_sentence_end format
            const sentence2Start = q.second_sentence_start || q.sentence2_start || (q.sentence2 ? q.sentence2.split('_____')[0] : '');
            const sentence2End = q.second_sentence_end || q.sentence2_end || (q.sentence2 ? q.sentence2.split('_____')[1] : '');
            const isCorrect = submitted && answers[q.id]?.toLowerCase().trim() === correctAnswer.toLowerCase();
            
            return (
              <div key={q.id} className="bg-white border border-slate-200 p-3 rounded-sm">
                <p className="font-bold text-slate-900 mb-2 text-sm">{questionNumber}.</p>
                <p className="text-slate-700 mb-2 text-sm">{firstSentence}</p>
                <div className="flex items-center gap-2 my-2">
                  <span className="bg-orange-100 text-orange-700 px-2 py-0.5 rounded font-mono uppercase text-xs">
                    {keyWord}
                  </span>
                </div>
                <p className="text-slate-600 mb-2 text-sm">{sentence2Start} <span className="text-orange-600 font-bold">_____</span> {sentence2End}</p>
                <Input
                  value={answers[q.id] || ''}
                  onChange={(e) => handleAnswerChange(q.id, e.target.value.toLowerCase())}
                  disabled={submitted}
                  placeholder="3-6 words..."
                  className={`w-full h-9 text-sm ${
                    submitted
                      ? isCorrect
                        ? 'bg-green-50 border-green-400'
                        : 'bg-red-50 border-red-400'
                      : ''
                  }`}
                />
                {submitted && (
                  <p className={`text-xs mt-1 ${isCorrect ? 'text-green-600' : 'text-red-600'}`}>
                    ✓ {correctAnswer}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Render Part 7: Multiple Matching (B2) - Texts on top, questions underneath
  const renderMultipleMatching = () => {
    const { sections, questions, instructions, instructions_es, title } = currentExercise.content;
    
    return (
      <div className="space-y-4">
        <div className="bg-blue-50 border border-blue-200 p-3 rounded-sm">
          <div className="flex items-start gap-2">
            <Info className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900 text-sm">{instructions}</p>
              <p className="text-xs text-blue-700 mt-1">{instructions_es}</p>
            </div>
          </div>
        </div>

        {/* Texts A-D in 2x2 grid - Full width */}
        <div className="bg-white border border-slate-200 p-4 rounded-sm">
          <h3 className="font-semibold text-slate-900 text-sm mb-3 pb-2 border-b border-slate-200">
            {title || "Texts / Textos"}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {sections.map(section => (
              <div key={section.id} className="bg-slate-50 border border-slate-200 p-3 rounded">
                <div className="flex items-center gap-2 mb-2">
                  <span className="bg-slate-900 text-white w-7 h-7 rounded-full flex items-center justify-center font-bold text-sm">
                    {section.id}
                  </span>
                  {section.title && (
                    <span className="font-medium text-slate-900 text-sm">{section.title}</span>
                  )}
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">{section.text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Questions underneath in 2-column grid */}
        <div className="bg-white border border-slate-200 p-4 rounded-sm">
          <h3 className="font-semibold text-slate-900 text-sm mb-3 pb-2 border-b border-slate-200">
            Questions / Preguntas
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {questions.map((q) => {
              const questionNum = q.number || 43;
              const isCorrect = submitted && answers[q.id] === q.answer;
              return (
                <div key={q.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded border border-slate-200">
                  <span className="font-bold text-slate-700 text-sm min-w-7">{questionNum}.</span>
                  <p className="flex-1 text-xs text-slate-600 leading-tight">{q.statement}</p>
                  <select
                    value={answers[q.id] || ''}
                    onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                    disabled={submitted}
                    className={`w-12 p-1.5 border rounded text-sm text-center ${
                      submitted
                        ? isCorrect
                          ? 'bg-green-50 border-green-400'
                          : 'bg-red-50 border-red-400'
                        : 'border-slate-300 bg-white'
                      }`}
                    >
                      <option value="">-</option>
                      {sections.map(s => (
                        <option key={s.id} value={s.id}>{s.id}</option>
                      ))}
                    </select>
                    {submitted && !isCorrect && (
                      <span className="text-xs text-green-600 font-medium">✓{q.answer}</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      );
  };

  // Render standard format (for non-B1 or legacy exercises)
  const renderStandardFormat = () => {
    const questions = currentExercise.content.questions || [];
    const title = currentExercise.content.title;
    return (
      <div className="flex flex-col gap-6">
        {/* Text */}
        <div className="bg-white border border-slate-200 p-6 rounded-sm">
          {title ? (
            <h3 className="font-bold text-lg text-slate-900 mb-4 pb-2 border-b border-slate-200">
              {title}
            </h3>
          ) : (
            <h3 className="font-semibold text-slate-900 mb-4">Text / Texto</h3>
          )}
          <div className="prose prose-sm max-w-none text-slate-700 whitespace-pre-line">
            {currentExercise.content.text}
          </div>
        </div>

        {/* Questions */}
        <div className="space-y-4">
          <h3 className="font-semibold text-slate-900">Questions / Preguntas</h3>
          {questions.map((q, idx) => (
            <div key={q.id} className="bg-white border border-slate-200 p-4 rounded-sm">
              <p className="font-medium text-slate-900 mb-1">{idx + 1}. {q.question}</p>
              <p className="text-sm text-slate-500 mb-3">{q.question_es}</p>
              
              <RadioGroup
                value={answers[q.id]?.toString() || ''}
                onValueChange={(value) => handleAnswerChange(q.id, parseInt(value))}
                disabled={submitted}
              >
                {q.options.map((option, optIdx) => (
                  <div key={optIdx} className={`flex items-center space-x-2 p-2 rounded-sm ${
                    submitted 
                      ? optIdx === q.correct 
                        ? 'bg-green-50' 
                        : answers[q.id] === optIdx 
                          ? 'bg-red-50' 
                          : ''
                      : 'hover:bg-slate-50'
                  }`}>
                    <RadioGroupItem value={optIdx.toString()} id={`${q.id}-${optIdx}`} />
                    <Label htmlFor={`${q.id}-${optIdx}`} className="flex-1 cursor-pointer text-sm">
                      {String.fromCharCode(65 + optIdx)}. {option}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderExerciseContent = () => {
    const partType = getPartType();
    
    switch (partType) {
      case 'short_messages':
        return renderPart1ShortMessages();
      case 'matching':
        return renderPart2Matching();
      case 'long_text_mc':
        return renderPart3LongText();
      case 'gapped_text':
        return renderPart4GappedText();
      case 'multiple_choice_cloze':
        return renderPart5Cloze();
      case 'open_cloze':
        return renderPart6OpenCloze();
      case 'word_formation':
        return renderWordFormation();
      case 'key_word_transformation':
        return renderKeyWordTransformation();
      case 'multiple_matching':
        return renderMultipleMatching();
      default:
        return renderStandardFormat();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="animate-pulse text-slate-600">Cargando ejercicios de lectura...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            <button 
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">Volver</span>
            </button>
            <div className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-orange-500" />
              <span className="font-medium text-slate-900">Reading & Use of English</span>
            </div>
            {user?.level && (
              <span className={`level-badge level-${user.level.toLowerCase()}`}>
                {user.level}
              </span>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Exercise List */}
        <ExerciseList
          exercises={exercises}
          currentExercise={currentExercise}
          onSelectExercise={handleSelectExercise}
          completedIds={completedIds}
        />

        {!currentExercise ? (
          <div className="text-center py-12">
            <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">
              No hay ejercicios disponibles
            </h2>
            <p className="text-slate-600 mb-4">
              {user?.level 
                ? `No encontramos ejercicios de lectura para el nivel ${user.level}`
                : 'Selecciona un nivel para ver los ejercicios'}
            </p>
            <Button onClick={() => navigate('/level-select')} className="rounded-full">
              Seleccionar Nivel
            </Button>
          </div>
        ) : (
          <>
            {/* Exercise Info */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h1 className="text-xl font-semibold text-slate-900">
                    {currentExercise.title}
                  </h1>
                  <p className="text-sm text-orange-600">{currentExercise.title_es}</p>
                </div>
                <span className={`level-badge level-${currentExercise.level?.toLowerCase()}`}>
                  {currentExercise.level}
                </span>
              </div>
              <p className="text-slate-600">{currentExercise.description}</p>
              <p className="text-sm text-slate-500">{currentExercise.description_es}</p>
            </div>

            {/* Exercise Content */}
            {renderExerciseContent()}

            {/* Submit / Next Buttons */}
            <div className="mt-8 flex justify-end gap-4">
              {!submitted ? (
                <Button
                  onClick={handleSubmit}
                  disabled={Object.keys(answers).length === 0}
                  className="bg-slate-900 hover:bg-slate-800 rounded-full px-8"
                  data-testid="submit-btn"
                >
                  Enviar Respuestas
                </Button>
              ) : (
                <>
                  {result && (
                    <div className="flex items-center gap-4 mr-auto">
                      <div className={`text-2xl font-bold ${
                        result.score >= 70 ? 'text-green-600' : 'text-orange-600'
                      }`}>
                        {result.score}%
                      </div>
                      <span className="text-slate-600">
                        {result.score >= 70 ? '¡Buen trabajo!' : 'Sigue practicando'}
                      </span>
                    </div>
                  )}
                  <Button
                    onClick={handleNextExercise}
                    className="bg-orange-600 hover:bg-orange-500 rounded-full px-8"
                    data-testid="next-btn"
                  >
                    Siguiente Ejercicio
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
